// src/pages/admin/AdminGrowthPage.jsx
// Phase 8 — M8: Admin Growth Engine console

import { useState, useEffect, useCallback } from 'react'

const PINK = '#FF3D7F', ORG = '#FF8500', DARK = '#1A1A2E', GREY = '#9CA3AF', GR = '#F3F4F6'

const api = (path, opts = {}) =>
  fetch(`/api/v1${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    ...opts,
  }).then(r => r.json())

const TABS = [['dash','📊 Dashboard'],['reviews','⭐ Reviews'],['promos','🎟 Promotions'],['flash','⚡ Flash Sales'],['analytics','📈 Analytics']]

function KPI({ label, value, sub, color = DARK }) {
  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,.07)', borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: '1.6rem', fontWeight: 900, color }}>{value}</div>
      <div style={{ fontWeight: 600, color: DARK, fontSize: '.85rem', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: '.75rem', color: GREY, marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function Stars({ rating }) {
  return (
    <span style={{ color: '#F59E0B', fontSize: '.9rem' }}>
      {'★'.repeat(Math.round(rating))}{'☆'.repeat(5 - Math.round(rating))}
      <span style={{ color: GREY, fontSize: '.8rem', marginLeft: 4 }}>{Number(rating).toFixed(1)}</span>
    </span>
  )
}

function StatusPill({ status, map }) {
  const cfg = map[status] ?? { bg: GR, color: GREY }
  return <span style={{ background: cfg.bg, color: cfg.color, padding: '2px 9px', borderRadius: 20, fontSize: '.72rem', fontWeight: 700 }}>{status}</span>
}

const REVIEW_STATUS = { pending: { bg: '#FEF3C7', color: '#92400E' }, approved: { bg: '#DCFCE7', color: '#166534' }, rejected: { bg: '#FEE2E2', color: '#991B1B' }, hidden: { bg: GR, color: GREY } }
const SALE_STATUS   = { active: { bg: '#DCFCE7', color: '#166534' }, scheduled: { bg: '#EDE9FE', color: '#5B21B6' }, ended: { bg: GR, color: GREY }, cancelled: { bg: '#FEE2E2', color: '#991B1B' } }

// ── Promotion form ─────────────────────────────────────────────────────────
const PROMO_DEFAULT = { name: '', code: '', type: 'percentage', value: '', min_order_value: 0, max_discount_inr: '', usage_limit: '', per_user_limit: 1, is_public: false, starts_at: '', expires_at: '', description: '' }
const FLASH_DEFAULT = { product_id: '', title: '', original_price_inr: '', sale_price_inr: '', stock_limit: '', starts_at: '', ends_at: '' }

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontFamily: 'Playfair Display,serif', fontWeight: 800, fontSize: '1.15rem', color: DARK, margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: GREY, fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '.8rem', fontWeight: 600, color: GREY, marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  )
}

const inp = { width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '.87rem', boxSizing: 'border-box' }

export default function AdminGrowthPage() {
  const [tab, setTab]         = useState('dash')
  const [dash, setDash]       = useState(null)
  const [reviews, setReviews] = useState([])
  const [promos, setPromos]   = useState([])
  const [flash, setFlash]     = useState([])
  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)

  const [promoModal, setPromoModal] = useState(false)
  const [flashModal, setFlashModal] = useState(false)
  const [promoForm, setPromoForm]   = useState(PROMO_DEFAULT)
  const [flashForm, setFlashForm]   = useState(FLASH_DEFAULT)
  const [saving, setSaving]         = useState(false)

  const [reviewFilter, setReviewFilter] = useState('pending')
  const [flashFilter,  setFlashFilter]  = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    const [d, r, p, f, a] = await Promise.all([
      api('/admin/growth/dashboard'),
      api(`/admin/growth/reviews?status=${reviewFilter}`),
      api('/admin/growth/promotions'),
      api(`/admin/growth/flash-sales${flashFilter ? `?status=${flashFilter}` : ''}`),
      api('/admin/growth/analytics'),
    ])
    setDash(d.data ?? null)
    setReviews(r.data?.data ?? [])
    setPromos(p.data?.data ?? [])
    setFlash(f.data?.data ?? [])
    setAnalytics(a.data ?? null)
    setLoading(false)
  }, [reviewFilter, flashFilter])

  useEffect(() => { load() }, [load])

  const moderateReview = async (id, status) => {
    await api(`/admin/growth/reviews/${id}/moderate`, { method: 'PATCH', body: JSON.stringify({ status }) })
    setReviews(prev => prev.filter(r => r.id !== id))
  }

  const createPromo = async () => {
    setSaving(true)
    const r = await api('/admin/growth/promotions', { method: 'POST', body: JSON.stringify(promoForm) })
    setSaving(false)
    if (r.status === 'success' || r.data) { setPromoModal(false); setPromoForm(PROMO_DEFAULT); load() }
    else alert(r.message ?? 'Failed')
  }

  const deactivatePromo = async (id) => {
    if (!confirm('Deactivate this promotion?')) return
    await api(`/admin/growth/promotions/${id}`, { method: 'DELETE' }); load()
  }

  const createFlash = async () => {
    setSaving(true)
    const r = await api('/admin/growth/flash-sales', { method: 'POST', body: JSON.stringify(flashForm) })
    setSaving(false)
    if (r.status === 'success' || r.data) { setFlashModal(false); setFlashForm(FLASH_DEFAULT); load() }
    else alert(r.message ?? 'Failed')
  }

  const cancelFlash = async (id) => {
    if (!confirm('Cancel this flash sale?')) return
    await api(`/admin/growth/flash-sales/${id}`, { method: 'DELETE' }); load()
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: GREY }}>Loading Growth Engine…</div>

  const rv = dash?.reviews    ?? {}
  const pr = dash?.promotions ?? {}
  const fs = dash?.flash_sales?? {}
  const rf = dash?.referrals  ?? {}

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 0 56px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Playfair Display,serif', fontWeight: 800, fontSize: '1.85rem', color: DARK, margin: 0 }}>Growth Engine</h1>
        <p style={{ color: GREY, margin: '4px 0 0', fontSize: '.88rem' }}>Reviews · Promotions · Flash Sales · Referrals · Supplier Analytics</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, background: GR, borderRadius: 12, padding: 4, width: 'fit-content', marginBottom: 24 }}>
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '.83rem', background: tab === k ? '#fff' : 'transparent', color: tab === k ? PINK : '#6B7280', boxShadow: tab === k ? '0 1px 4px rgba(0,0,0,.08)' : 'none' }}>{l}</button>
        ))}
      </div>

      {/* ── DASHBOARD ── */}
      {tab === 'dash' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 24 }}>
            <KPI label="Reviews Pending"    value={rv.pending  ?? 0} color={ORG}     sub={`${rv.total ?? 0} total · avg ★${rv.avg_rating ?? 0}`} />
            <KPI label="Active Promotions"  value={pr.active   ?? 0} color={PINK}    sub={`₹${Number(pr.total_discount_given ?? 0).toLocaleString('en-IN')} saved`} />
            <KPI label="Flash Sales Live"   value={fs.active   ?? 0} color="#10B981" sub={`${fs.total_units_sold ?? 0} units sold`} />
            <KPI label="Referral Conversions" value={rf.total_conversions ?? 0} color="#6366F1" sub={`₹${Number(rf.total_rewards_inr ?? 0).toLocaleString('en-IN')} rewards`} />
          </div>

          {/* Top promo codes */}
          {(pr.top_codes ?? []).length > 0 && (
            <div style={{ background: '#fff', borderRadius: 18, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.07)', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, color: DARK, marginBottom: 14, fontSize: '.9rem' }}>🏆 Top Promotion Codes</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {pr.top_codes.map(c => (
                  <div key={c.code} style={{ background: GR, padding: '10px 16px', borderRadius: 12, minWidth: 140 }}>
                    <div style={{ fontFamily: 'monospace', fontWeight: 800, color: PINK, fontSize: '.9rem' }}>{c.code || '(auto)'}</div>
                    <div style={{ fontSize: '.78rem', color: GREY, marginTop: 2 }}>{c.name}</div>
                    <div style={{ fontWeight: 700, color: DARK, marginTop: 4 }}>{c.used_count} uses</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top suppliers */}
          {(analytics?.top_suppliers_today ?? []).length > 0 && (
            <div style={{ background: '#fff', borderRadius: 18, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.07)' }}>
              <div style={{ fontWeight: 700, color: DARK, marginBottom: 14, fontSize: '.9rem' }}>📈 Top Suppliers Today</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: GR }}>{['Supplier','GMV','Orders','Rating'].map(h => <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: '.75rem', fontWeight: 700, color: GREY, textTransform: 'uppercase' }}>{h}</th>)}</tr></thead>
                <tbody>
                  {analytics.top_suppliers_today.map((s, i) => (
                    <tr key={s.supplier_id} style={{ borderBottom: '1px solid #F9FAFB', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: DARK, fontSize: '.85rem' }}>{s.supplier?.company_name ?? s.supplier?.name ?? `#${s.supplier_id}`}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: PINK, fontSize: '.85rem' }}>₹{Number(s.gmv_inr).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '10px 12px', color: GREY, fontSize: '.85rem' }}>{s.orders_count}</td>
                      <td style={{ padding: '10px 12px' }}><Stars rating={s.avg_review_rating ?? 0} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── REVIEWS ── */}
      {tab === 'reviews' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {['pending','approved','rejected','hidden'].map(s => (
              <button key={s} onClick={() => setReviewFilter(s)} style={{ padding: '6px 14px', borderRadius: 10, border: `1.5px solid ${reviewFilter === s ? PINK : '#E5E7EB'}`, background: reviewFilter === s ? PINK : '#fff', color: reviewFilter === s ? '#fff' : '#6B7280', fontWeight: 600, fontSize: '.8rem', cursor: 'pointer' }}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          {reviews.length === 0
            ? <div style={{ padding: 40, textAlign: 'center', color: GREY, background: '#fff', borderRadius: 16 }}>No {reviewFilter} reviews.</div>
            : reviews.map(r => (
              <div key={r.id} style={{ background: '#fff', borderRadius: 16, padding: 22, boxShadow: '0 1px 4px rgba(0,0,0,.07)', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <Stars rating={r.rating} />
                    <span style={{ marginLeft: 8, fontSize: '.82rem', color: GREY }}>{r.reviewable_type} #{r.reviewable_id}</span>
                    {r.is_verified_purchase && <span style={{ marginLeft: 8, background: '#DCFCE7', color: '#166534', padding: '1px 7px', borderRadius: 20, fontSize: '.7rem', fontWeight: 700 }}>✓ Verified</span>}
                    {r.flag_count > 0 && <span style={{ marginLeft: 8, background: '#FEE2E2', color: '#991B1B', padding: '1px 7px', borderRadius: 20, fontSize: '.7rem', fontWeight: 700 }}>🚩 {r.flag_count} flags</span>}
                  </div>
                  <StatusPill status={r.status} map={REVIEW_STATUS} />
                </div>
                {r.title && <div style={{ fontWeight: 700, color: DARK, marginBottom: 4, fontSize: '.9rem' }}>{r.title}</div>}
                <div style={{ fontSize: '.85rem', color: '#4B5563', lineHeight: 1.5, marginBottom: 12 }}>{r.body}</div>
                <div style={{ fontSize: '.78rem', color: GREY, marginBottom: 12 }}>by {r.reviewer?.name ?? `User #${r.reviewer_id}`} · {r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN') : ''}</div>
                {reviewFilter === 'pending' && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => moderateReview(r.id, 'approved')} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#10B981', color: '#fff', fontWeight: 700, fontSize: '.8rem', cursor: 'pointer' }}>✓ Approve</button>
                    <button onClick={() => moderateReview(r.id, 'rejected')} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#EF4444', color: '#fff', fontWeight: 700, fontSize: '.8rem', cursor: 'pointer' }}>✗ Reject</button>
                    <button onClick={() => moderateReview(r.id, 'hidden')}   style={{ padding: '6px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', background: '#fff', color: GREY, fontWeight: 600, fontSize: '.8rem', cursor: 'pointer' }}>Hide</button>
                  </div>
                )}
              </div>
            ))
          }
        </div>
      )}

      {/* ── PROMOTIONS ── */}
      {tab === 'promos' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button onClick={() => setPromoModal(true)} style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '.85rem' }}>+ New Promotion</button>
          </div>

          <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.07)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: GR }}>
                {['Code / Name','Type','Value','Min Order','Usage','Expires','Actions'].map(h => <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '.75rem', fontWeight: 700, color: GREY, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {promos.length === 0 && <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: GREY }}>No promotions yet.</td></tr>}
                {promos.map((p, i) => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #F9FAFB', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                    <td style={{ padding: '12px 14px' }}>
                      {p.code && <div style={{ fontFamily: 'monospace', fontWeight: 800, color: PINK, fontSize: '.87rem' }}>{p.code}</div>}
                      <div style={{ fontWeight: 600, color: DARK, fontSize: '.83rem' }}>{p.name}</div>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: '.82rem', color: GREY, textTransform: 'capitalize' }}>{p.type}</td>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: DARK, fontSize: '.85rem' }}>{p.type === 'percentage' ? `${p.value}%` : `₹${p.value}`}</td>
                    <td style={{ padding: '12px 14px', fontSize: '.82rem', color: GREY }}>₹{Number(p.min_order_value).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px', fontSize: '.82rem', color: GREY }}>{p.used_count}{p.usage_limit ? `/${p.usage_limit}` : ' / ∞'}</td>
                    <td style={{ padding: '12px 14px', fontSize: '.8rem', color: GREY, whiteSpace: 'nowrap' }}>{p.expires_at ? new Date(p.expires_at).toLocaleDateString('en-IN') : '—'}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ background: p.is_active ? '#DCFCE7' : '#FEE2E2', color: p.is_active ? '#166534' : '#991B1B', padding: '2px 8px', borderRadius: 20, fontSize: '.72rem', fontWeight: 700, marginRight: 8 }}>{p.is_active ? 'Active' : 'Inactive'}</span>
                      {p.is_active && <button onClick={() => deactivatePromo(p.id)} style={{ padding: '4px 8px', borderRadius: 7, border: 'none', background: '#FEE2E2', color: '#991B1B', fontWeight: 700, fontSize: '.72rem', cursor: 'pointer' }}>Off</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {promoModal && (
            <Modal title="New Promotion" onClose={() => setPromoModal(false)}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Name *"><input style={inp} value={promoForm.name} onChange={e => setPromoForm(f => ({...f, name: e.target.value}))} /></Field>
                  <Field label="Code (leave blank = auto-apply)"><input style={inp} value={promoForm.code} onChange={e => setPromoForm(f => ({...f, code: e.target.value}))} /></Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Type *">
                    <select style={inp} value={promoForm.type} onChange={e => setPromoForm(f => ({...f, type: e.target.value}))}>
                      {['percentage','flat','free_shipping','bogo','bulk'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label={promoForm.type === 'percentage' ? 'Discount %' : 'Discount ₹'}>
                    <input type="number" style={inp} value={promoForm.value} onChange={e => setPromoForm(f => ({...f, value: e.target.value}))} />
                  </Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Min Order Value (₹)"><input type="number" style={inp} value={promoForm.min_order_value} onChange={e => setPromoForm(f => ({...f, min_order_value: e.target.value}))} /></Field>
                  <Field label="Max Discount (₹, optional)"><input type="number" style={inp} value={promoForm.max_discount_inr} onChange={e => setPromoForm(f => ({...f, max_discount_inr: e.target.value}))} /></Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Total Usage Limit (blank = ∞)"><input type="number" style={inp} value={promoForm.usage_limit} onChange={e => setPromoForm(f => ({...f, usage_limit: e.target.value}))} /></Field>
                  <Field label="Per User Limit"><input type="number" style={inp} value={promoForm.per_user_limit} onChange={e => setPromoForm(f => ({...f, per_user_limit: e.target.value}))} /></Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Starts At"><input type="datetime-local" style={inp} value={promoForm.starts_at} onChange={e => setPromoForm(f => ({...f, starts_at: e.target.value}))} /></Field>
                  <Field label="Expires At"><input type="datetime-local" style={inp} value={promoForm.expires_at} onChange={e => setPromoForm(f => ({...f, expires_at: e.target.value}))} /></Field>
                </div>
                <Field label="Description"><textarea style={{...inp, height: 60}} value={promoForm.description} onChange={e => setPromoForm(f => ({...f, description: e.target.value}))} /></Field>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={promoForm.is_public} onChange={e => setPromoForm(f => ({...f, is_public: e.target.checked}))} style={{ accentColor: PINK, width: 16, height: 16 }} />
                  <span style={{ fontSize: '.87rem', color: DARK, fontWeight: 600 }}>Show on public promotions page</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                <button onClick={() => setPromoModal(false)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button onClick={createPromo} disabled={saving} style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: saving ? '#D1D5DB' : PINK, color: '#fff', fontWeight: 700, cursor: saving ? 'default' : 'pointer' }}>{saving ? 'Saving…' : 'Create Promotion'}</button>
              </div>
            </Modal>
          )}
        </div>
      )}

      {/* ── FLASH SALES ── */}
      {tab === 'flash' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {['','active','scheduled','ended'].map(s => (
                <button key={s} onClick={() => setFlashFilter(s)} style={{ padding: '6px 14px', borderRadius: 10, border: `1.5px solid ${flashFilter === s ? PINK : '#E5E7EB'}`, background: flashFilter === s ? PINK : '#fff', color: flashFilter === s ? '#fff' : '#6B7280', fontWeight: 600, fontSize: '.8rem', cursor: 'pointer' }}>
                  {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <button onClick={() => setFlashModal(true)} style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: ORG, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '.85rem' }}>⚡ New Flash Sale</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 14 }}>
            {flash.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: GREY, background: '#fff', borderRadius: 16, gridColumn: '1/-1' }}>No flash sales.</div>}
            {flash.map(s => (
              <div key={s.id} style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,.07)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <StatusPill status={s.status} map={SALE_STATUS} />
                  <span style={{ background: '#FFF7ED', color: ORG, padding: '2px 9px', borderRadius: 20, fontSize: '.72rem', fontWeight: 800 }}>{s.discount_pct}% OFF</span>
                </div>
                <div style={{ fontWeight: 700, color: DARK, fontSize: '.95rem', marginBottom: 4 }}>{s.title}</div>
                <div style={{ fontSize: '.8rem', color: GREY, marginBottom: 10 }}>Product #{s.product_id}</div>
                <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                  <div><div style={{ fontWeight: 800, color: PINK, fontSize: '1.1rem' }}>₹{Number(s.sale_price_inr).toLocaleString('en-IN')}</div><div style={{ textDecoration: 'line-through', fontSize: '.78rem', color: GREY }}>₹{Number(s.original_price_inr).toLocaleString('en-IN')}</div></div>
                  <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, color: DARK }}>{s.units_sold} sold</div>
                    {s.stock_limit && <div style={{ fontSize: '.78rem', color: GREY }}>of {s.stock_limit}</div>}
                  </div>
                </div>
                <div style={{ fontSize: '.78rem', color: GREY, marginBottom: 12 }}>
                  {new Date(s.starts_at).toLocaleString('en-IN',{dateStyle:'short',timeStyle:'short'})} → {new Date(s.ends_at).toLocaleString('en-IN',{dateStyle:'short',timeStyle:'short'})}
                </div>
                {['active','scheduled'].includes(s.status) && (
                  <button onClick={() => cancelFlash(s.id)} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#FEE2E2', color: '#991B1B', fontWeight: 700, fontSize: '.78rem', cursor: 'pointer' }}>Cancel Sale</button>
                )}
              </div>
            ))}
          </div>

          {flashModal && (
            <Modal title="⚡ New Flash Sale" onClose={() => setFlashModal(false)}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Product ID *"><input type="number" style={inp} value={flashForm.product_id} onChange={e => setFlashForm(f => ({...f, product_id: e.target.value}))} /></Field>
                  <Field label="Title *"><input style={inp} value={flashForm.title} onChange={e => setFlashForm(f => ({...f, title: e.target.value}))} /></Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <Field label="Original Price (₹) *"><input type="number" style={inp} value={flashForm.original_price_inr} onChange={e => setFlashForm(f => ({...f, original_price_inr: e.target.value}))} /></Field>
                  <Field label="Sale Price (₹) *"><input type="number" style={inp} value={flashForm.sale_price_inr} onChange={e => setFlashForm(f => ({...f, sale_price_inr: e.target.value}))} /></Field>
                  <Field label="Stock Limit (blank = ∞)"><input type="number" style={inp} value={flashForm.stock_limit} onChange={e => setFlashForm(f => ({...f, stock_limit: e.target.value}))} /></Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Starts At *"><input type="datetime-local" style={inp} value={flashForm.starts_at} onChange={e => setFlashForm(f => ({...f, starts_at: e.target.value}))} /></Field>
                  <Field label="Ends At *"><input type="datetime-local" style={inp} value={flashForm.ends_at} onChange={e => setFlashForm(f => ({...f, ends_at: e.target.value}))} /></Field>
                </div>
                {flashForm.original_price_inr && flashForm.sale_price_inr && +flashForm.original_price_inr > 0 && (
                  <div style={{ background: '#FFF7ED', borderRadius: 10, padding: '10px 14px', fontSize: '.85rem', color: ORG, fontWeight: 700 }}>
                    Discount: {Math.round((1 - flashForm.sale_price_inr / flashForm.original_price_inr) * 100)}% off
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                <button onClick={() => setFlashModal(false)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button onClick={createFlash} disabled={saving} style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: saving ? '#D1D5DB' : ORG, color: '#fff', fontWeight: 700, cursor: saving ? 'default' : 'pointer' }}>{saving ? 'Saving…' : '⚡ Schedule Flash Sale'}</button>
              </div>
            </Modal>
          )}
        </div>
      )}

      {/* ── ANALYTICS ── */}
      {tab === 'analytics' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 24 }}>
            <KPI label="Total GMV Today"    value={`₹${Number(analytics?.total_gmv_today ?? 0).toLocaleString('en-IN')}`} color={PINK} />
            <KPI label="Total Orders Today" value={analytics?.total_orders_today ?? 0}    color={ORG} />
            <KPI label="Avg Supplier Rating" value={`★ ${analytics?.avg_supplier_rating ?? 0}`} color="#F59E0B" />
          </div>

          {(analytics?.top_suppliers_today ?? []).length > 0 && (
            <div style={{ background: '#fff', borderRadius: 18, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.07)' }}>
              <div style={{ fontWeight: 700, color: DARK, marginBottom: 14, fontSize: '.9rem' }}>Top Performing Suppliers — Today</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: GR }}>
                  {['#','Supplier','GMV','Orders','Avg Rating'].map(h => <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '.75rem', fontWeight: 700, color: GREY, textTransform: 'uppercase' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {analytics.top_suppliers_today.map((s, i) => (
                    <tr key={s.supplier_id} style={{ borderBottom: '1px solid #F9FAFB', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 800, color: GREY, fontSize: '.85rem' }}>#{i+1}</td>
                      <td style={{ padding: '10px 14px', fontWeight: 600, color: DARK, fontSize: '.85rem' }}>{s.supplier?.company_name ?? s.supplier?.name ?? `#${s.supplier_id}`}</td>
                      <td style={{ padding: '10px 14px', fontWeight: 700, color: PINK }}>₹{Number(s.gmv_inr).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '10px 14px', color: GREY }}>{s.orders_count}</td>
                      <td style={{ padding: '10px 14px' }}><Stars rating={s.avg_review_rating ?? 0} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
